import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Separator } from '../ui/separator';
import { formatCurrency } from '../../lib/utils';
import { Calculator, Percent, DollarSign, ChevronDown, ChevronUp } from 'lucide-react';

const PricingStep = ({ formData, setFormData, calculateAmounts }) => {
  const [expandedSections, setExpandedSections] = useState({
    discount: false,
    vat: false,
    withholdingTax: false,
    summary: true
  });

  // Handle nested input changes for JSON fields
  const handleNestedInputChange = (field, key, value) => {
    setFormData(prev => {
      const currentValue = prev[field];
      let parsedValue;
      
      try {
        parsedValue = typeof currentValue === 'string' ? JSON.parse(currentValue) : currentValue;
      } catch (e) {
        parsedValue = {};
      }
      
      const updatedValue = { ...parsedValue, [key]: value };
      
      return {
        ...prev,
        [field]: updatedValue
      };
    });
  };

  // Toggle section expansion
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Calculate amounts for display
  const amounts = calculateAmounts();

  return (
    <div className="space-y-4">
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-100 rounded-full mb-3">
          <Calculator className="h-6 w-6 text-blue-600" />
        </div>
        <h2 className="text-2xl font-bold text-foreground mb-1">การตั้งราคา</h2>
        <p className="text-muted-foreground text-sm">กำหนดราคาและภาษีสำหรับโปรเจค</p>
      </div>

      {/* Project Price */}
      <Card className="border-2 border-blue-200 hover:border-blue-300 transition-colors">
        <CardHeader className="bg-blue-50 py-3">
          <CardTitle className="flex items-center space-x-2 text-blue-800">
            <DollarSign className="h-5 w-5" />
            <span>ราคาโปรเจค</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 pt-4">
          {/* Price Type Selection */}
          <div className="space-y-3">
            <Label className="text-sm font-medium text-gray-700">
              ประเภทราคา:
            </Label>
            <div className="grid grid-cols-2 gap-3">
              <div 
                className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                  formData.price_type === 'exclude_vat' 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                }`}
                onClick={() => setFormData(prev => ({ ...prev, price_type: 'exclude_vat' }))}
              >
                <div className="flex items-center space-x-2">
                  <div className={`w-4 h-4 rounded-full border-2 ${
                    formData.price_type === 'exclude_vat' 
                      ? 'border-blue-500 bg-blue-500' 
                      : 'border-gray-300'
                  }`}>
                    {formData.price_type === 'exclude_vat' && (
                      <div className="w-2 h-2 bg-white rounded-full m-0.5"></div>
                    )}
                  </div>
                  <span className={`text-sm font-medium ${
                    formData.price_type === 'exclude_vat' ? 'text-blue-700' : 'text-gray-600'
                  }`}>
                    ไม่รวม VAT
                  </span>
                </div>
                <p className={`text-xs mt-1 ${
                  formData.price_type === 'exclude_vat' ? 'text-blue-600' : 'text-gray-500'
                }`}>
                  ราคาที่แสดงไม่รวมภาษีมูลค่าเพิ่ม
                </p>
              </div>
              
              <div 
                className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                  formData.price_type === 'include_vat' 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                }`}
                onClick={() => setFormData(prev => ({ ...prev, price_type: 'include_vat' }))}
              >
                <div className="flex items-center space-x-2">
                  <div className={`w-4 h-4 rounded-full border-2 ${
                    formData.price_type === 'include_vat' 
                      ? 'border-blue-500 bg-blue-500' 
                      : 'border-gray-300'
                  }`}>
                    {formData.price_type === 'include_vat' && (
                      <div className="w-2 h-2 bg-white rounded-full m-0.5"></div>
                    )}
                  </div>
                  <span className={`text-sm font-medium ${
                    formData.price_type === 'include_vat' ? 'text-blue-700' : 'text-gray-600'
                  }`}>
                    รวม VAT
                  </span>
                </div>
                <p className={`text-xs mt-1 ${
                  formData.price_type === 'include_vat' ? 'text-blue-600' : 'text-gray-500'
                }`}>
                  ราคาที่แสดงรวมภาษีมูลค่าเพิ่มแล้ว
                </p>
              </div>
            </div>
          </div>

          <Separator />

          <div className="space-y-2">
            <Label htmlFor="project_price" className="text-sm font-medium">
              ราคาโปรเจค (฿):
            </Label>
            <Input
              id="project_price"
              type="number"
              placeholder="100000"
              value={formData.project_price || ''}
              onChange={(e) => {
                const value = parseFloat(e.target.value) || 0;
                const safeValue = Math.max(value, 0);
                setFormData(prev => ({ ...prev, project_price: safeValue }));
              }}
              className="w-full h-10"
              step="0.01"
              min="0"
            />
            <p className="text-xs text-gray-500">
              {formData.price_type === 'exclude_vat' 
                ? 'กรอกราคาไม่รวม VAT (VAT จะถูกคำนวณเพิ่มในภายหลัง)'
                : 'กรอกราคารวม VAT แล้ว (VAT จะถูกแยกออกในภายหลัง)'
              }
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Discount */}
      <Card className="border-2 border-yellow-200 hover:border-yellow-300 transition-colors">
        <CardHeader 
          className="bg-yellow-50 py-3 cursor-pointer"
          onClick={() => toggleSection('discount')}
        >
          <CardTitle className="flex items-center justify-between text-yellow-800">
            <div className="flex items-center space-x-2">
              <Percent className="h-5 w-5" />
              <span>ส่วนลด</span>
            </div>
            <div className="flex items-center space-x-3">
              <Switch
                checked={(() => {
                  try {
                    const discount = typeof formData.discount === 'string' ? JSON.parse(formData.discount) : formData.discount;
                    return discount?.enabled || false;
                  } catch (e) {
                    return false;
                  }
                })()}
                onChange={(e) => handleNestedInputChange('discount', 'enabled', e.target.checked)}
              />
              {expandedSections.discount ? (
                <ChevronUp className="h-5 w-5" />
              ) : (
                <ChevronDown className="h-5 w-5" />
              )}
            </div>
          </CardTitle>
        </CardHeader>
        {expandedSections.discount && (
          <CardContent className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label htmlFor="discount_percentage" className="text-sm font-medium">
                ส่วนลด {(() => {
                  try {
                    const discount = typeof formData.discount === 'string' ? JSON.parse(formData.discount) : formData.discount;
                    return discount?.percentage || 0;
                  } catch (e) {
                    return 0;
                  }
                })()}%:
              </Label>
              <Input
                id="discount_percentage"
                type="number"
                placeholder="0"
                value={(() => {
                  try {
                    const discount = typeof formData.discount === 'string' ? JSON.parse(formData.discount) : formData.discount;
                    return discount?.percentage || 0;
                  } catch (e) {
                    return 0;
                  }
                })()}
                onChange={(e) => {
                  const value = parseFloat(e.target.value) || 0;
                  const safeValue = Math.min(Math.max(value, 0), 100);
                  handleNestedInputChange('discount', 'percentage', safeValue);
                  // ตั้งค่า type และ enabled เมื่อกรอกข้อมูล
                  if (safeValue > 0) {
                    handleNestedInputChange('discount', 'type', 'percentage');
                    handleNestedInputChange('discount', 'enabled', true);
                  } else {
                    handleNestedInputChange('discount', 'enabled', false);
                  }
                }}
                className="w-full h-10"
                step="0.01"
                min="0"
                max="100"
              />
            </div>
          </CardContent>
        )}
      </Card>

      {/* VAT */}
      <Card className="border-2 border-green-200 hover:border-green-300 transition-colors">
        <CardHeader 
          className="bg-green-50 py-3 cursor-pointer"
          onClick={() => toggleSection('vat')}
        >
          <CardTitle className="flex items-center justify-between text-green-800">
            <div className="flex items-center space-x-2">
              <Percent className="h-5 w-5" />
              <span>ภาษีมูลค่าเพิ่ม (VAT)</span>
            </div>
            <div className="flex items-center space-x-3">
              <Switch
                checked={(() => {
                  try {
                    const vat = typeof formData.vat === 'string' ? JSON.parse(formData.vat) : formData.vat;
                    return vat?.enabled || false;
                  } catch (e) {
                    return false;
                  }
                })()}
                onChange={(e) => handleNestedInputChange('vat', 'enabled', e.target.checked)}
              />
              {expandedSections.vat ? (
                <ChevronUp className="h-5 w-5" />
              ) : (
                <ChevronDown className="h-5 w-5" />
              )}
            </div>
          </CardTitle>
        </CardHeader>
        {expandedSections.vat && (
          <CardContent className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label htmlFor="vat_percentage" className="text-sm font-medium">
                VAT {(() => {
                  try {
                    const vat = typeof formData.vat === 'string' ? JSON.parse(formData.vat) : formData.vat;
                    return vat?.percentage || 0;
                  } catch (e) {
                    return 0;
                  }
                })()}%:
              </Label>
              <Input
                id="vat_percentage"
                type="number"
                placeholder="7"
                value={(() => {
                  try {
                    const vat = typeof formData.vat === 'string' ? JSON.parse(formData.vat) : formData.vat;
                    return vat?.percentage || 0;
                  } catch (e) {
                    return 0;
                  }
                })()}
                onChange={(e) => {
                  const value = parseFloat(e.target.value) || 0;
                  const safeValue = Math.min(Math.max(value, 0), 100);
                  handleNestedInputChange('vat', 'percentage', safeValue);
                }}
                className="w-full h-10"
                step="0.01"
                min="0"
                max="100"
              />
            </div>
          </CardContent>
        )}
      </Card>

      {/* Withholding Tax */}
      <Card className="border-2 border-orange-200 hover:border-orange-300 transition-colors">
        <CardHeader 
          className="bg-orange-50 py-3 cursor-pointer"
          onClick={() => toggleSection('withholdingTax')}
        >
          <CardTitle className="flex items-center justify-between text-orange-800">
            <div className="flex items-center space-x-2">
              <Percent className="h-5 w-5" />
              <span>ภาษีหัก ณ ที่จ่าย (WHT)</span>
            </div>
            <div className="flex items-center space-x-3">
              <Switch
                checked={(() => {
                  try {
                    const withholdingTax = typeof formData.withholding_tax === 'string' ? JSON.parse(formData.withholding_tax) : formData.withholding_tax;
                    return withholdingTax?.enabled || false;
                  } catch (e) {
                    return false;
                  }
                })()}
                onChange={(e) => handleNestedInputChange('withholding_tax', 'enabled', e.target.checked)}
              />
              {expandedSections.withholdingTax ? (
                <ChevronUp className="h-5 w-5" />
              ) : (
                <ChevronDown className="h-5 w-5" />
              )}
            </div>
          </CardTitle>
        </CardHeader>
        {expandedSections.withholdingTax && (
          <CardContent className="space-y-4 pt-4">
            {/* ประเภทภาษีหัก ณ ที่จ่าย */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">ประเภทภาษีหัก ณ ที่จ่าย</Label>
              <div className="flex space-x-4">
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id="wht_type_percentage"
                    name="wht_type"
                    value="percentage"
                    checked={(() => {
                      try {
                        const withholdingTax = typeof formData.withholding_tax === 'string' ? JSON.parse(formData.withholding_tax) : formData.withholding_tax;
                        return withholdingTax?.type === 'percentage' || withholdingTax?.type === undefined;
                      } catch (e) {
                        return true;
                      }
                    })()}
                    onChange={(e) => handleNestedInputChange('withholding_tax', 'type', e.target.value)}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="wht_type_percentage" className="text-sm">เปอร์เซ็นต์ (%)</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id="wht_type_amount"
                    name="wht_type"
                    value="amount"
                    checked={(() => {
                      try {
                        const withholdingTax = typeof formData.withholding_tax === 'string' ? JSON.parse(formData.withholding_tax) : formData.withholding_tax;
                        return withholdingTax?.type === 'amount';
                      } catch (e) {
                        return false;
                      }
                    })()}
                    onChange={(e) => handleNestedInputChange('withholding_tax', 'type', e.target.value)}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="wht_type_amount" className="text-sm">จำนวนเงิน (฿)</Label>
                </div>
              </div>
            </div>

            {/* Input สำหรับเปอร์เซ็นต์ */}
            {(() => {
              try {
                const withholdingTax = typeof formData.withholding_tax === 'string' ? JSON.parse(formData.withholding_tax) : formData.withholding_tax;
                return withholdingTax?.type === 'percentage' || withholdingTax?.type === undefined;
              } catch (e) {
                return true;
              }
            })() && (
              <div className="space-y-2">
                <Label htmlFor="withholding_tax_percentage" className="text-sm font-medium">
                  WHT {(() => {
                    try {
                      const withholdingTax = typeof formData.withholding_tax === 'string' ? JSON.parse(formData.withholding_tax) : formData.withholding_tax;
                      return withholdingTax?.percentage || 0;
                    } catch (e) {
                      return 0;
                    }
                  })()}%:
                </Label>
                <Input
                  id="withholding_tax_percentage"
                  type="number"
                  placeholder="3"
                  value={(() => {
                    try {
                      const withholdingTax = typeof formData.withholding_tax === 'string' ? JSON.parse(formData.withholding_tax) : formData.withholding_tax;
                      return withholdingTax?.percentage || 0;
                    } catch (e) {
                      return 0;
                    }
                  })()}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value) || 0;
                    const safeValue = Math.min(Math.max(value, 0), 100);
                    handleNestedInputChange('withholding_tax', 'percentage', safeValue);
                  }}
                  className="w-full h-10"
                  step="0.01"
                  min="0"
                  max="100"
                />
              </div>
            )}

            {/* Input สำหรับจำนวนเงิน */}
            {(() => {
              try {
                const withholdingTax = typeof formData.withholding_tax === 'string' ? JSON.parse(formData.withholding_tax) : formData.withholding_tax;
                return withholdingTax?.type === 'amount';
              } catch (e) {
                return false;
              }
            })() && (
              <div className="space-y-2">
                <Label htmlFor="withholding_tax_amount" className="text-sm font-medium">
                  จำนวนเงิน WHT (฿):
                </Label>
                <Input
                  id="withholding_tax_amount"
                  type="number"
                  placeholder="0"
                  value={(() => {
                    try {
                      const withholdingTax = typeof formData.withholding_tax === 'string' ? JSON.parse(formData.withholding_tax) : formData.withholding_tax;
                      return withholdingTax?.amount || 0;
                    } catch (e) {
                      return 0;
                    }
                  })()}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value) || 0;
                    const safeValue = Math.max(value, 0);
                    handleNestedInputChange('withholding_tax', 'amount', safeValue);
                  }}
                  className="w-full h-10"
                  step="0.01"
                  min="0"
                />
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* Calculation Summary */}
      <Card className="border-2 border-purple-200 hover:border-purple-300 transition-colors">
        <CardHeader 
          className="bg-purple-50 py-3 cursor-pointer"
          onClick={() => toggleSection('summary')}
        >
          <CardTitle className="flex items-center justify-between text-purple-800">
            <div className="flex items-center space-x-2">
              <Calculator className="h-5 w-5" />
              <span>สรุปการคำนวณ</span>
            </div>
            {expandedSections.summary ? (
              <ChevronUp className="h-5 w-5" />
            ) : (
              <ChevronDown className="h-5 w-5" />
            )}
          </CardTitle>
        </CardHeader>
        {expandedSections.summary && amounts && (
          <CardContent className="space-y-4 pt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">ราคาโปรเจค:</span>
                  <span className="font-medium">{formatCurrency(amounts.projectPrice)}</span>
                </div>
                {amounts.discountAmount > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">ส่วนลด:</span>
                    <span className="font-medium text-red-600">-{formatCurrency(amounts.discountAmount)}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-muted-foreground">ราคาหลังหักส่วนลด:</span>
                  <span className="font-medium">{formatCurrency(amounts.priceAfterDiscount)}</span>
                </div>
                {amounts.vatAmount > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">VAT:</span>
                    <span className="font-medium text-green-600">+{formatCurrency(amounts.vatAmount)}</span>
                  </div>
                )}
                <Separator />
                <div className="flex justify-between bg-blue-50 p-3 rounded-lg">
                  <span className="font-semibold text-blue-800">ราคารวม:</span>
                  <span className="font-bold text-blue-800 text-lg">{formatCurrency(amounts.finalAmount)}</span>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">WHT:</span>
                  <span className="font-medium text-orange-600">-{formatCurrency(amounts.withholdingTaxAmount)}</span>
                </div>
                <Separator />
                <div className="flex justify-between bg-green-50 p-3 rounded-lg">
                  <span className="font-semibold text-green-800">เงินเข้าจริง:</span>
                  <span className="font-bold text-green-800 text-lg">{formatCurrency(amounts.actualReceived)}</span>
                </div>
              </div>
            </div>
            
            {/* แสดงรายละเอียดการคำนวณเพิ่มเติม */}
            <Separator />
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-semibold text-gray-800 mb-3">รายละเอียดการคำนวณ</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">ราคาโปรเจค:</span>
                    <span className="font-medium">{formatCurrency(amounts.projectPrice)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">ส่วนลด ({amounts.discountPercentage || 0}%):</span>
                    <span className="font-medium text-red-600">-{formatCurrency(amounts.discountAmount)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">ราคาหลังหักส่วนลด:</span>
                    <span className="font-medium">{formatCurrency(amounts.priceAfterDiscount)}</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">VAT ({amounts.vatPercentage || 0}%):</span>
                    <span className="font-medium text-green-600">+{formatCurrency(amounts.vatAmount)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">WHT ({amounts.withholdingTaxPercentage || 0}%):</span>
                    <span className="font-medium text-orange-600">-{formatCurrency(amounts.withholdingTaxAmount)}</span>
                  </div>
                  <div className="flex justify-between font-semibold">
                    <span className="text-gray-800">ยอดรับโอน:</span>
                    <span className="text-green-700">{formatCurrency(amounts.actualReceived)}</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        )}
      </Card>
    </div>
  );
};

export default PricingStep;